import React, { useContext, useMemo } from 'react'

import { IconUser, IconUserPlus } from '@tabler/icons-react'
import { Space, Tooltip } from 'antd'
import Text from 'antd/lib/typography/Text'
import styled from 'styled-components'

import { ContactCard } from '@/components/shared-components/ContactCard'
import TableLink from '@/components/shared-components/TableLink'
import { ProjectContext } from '@/context/projectContext'

type Contact = {
  id?: string
  name?: string
  email?: string
  number?: string
  isPrimaryContact: boolean
  projects?: {
    id: string
  }[]
}

type ContactListProps = {
  onClickViewContacts?: () => void
  onClickAddContacts?: () => void
  onClickEditContact?: (contactId: string) => void
  contacts: Contact[] | undefined
  showMobile: boolean
  showAllProjectContacts: boolean
}

const LinkSpan = styled.span`
  margin-left: 0.5rem !important;

  text-decoration: none;
  cursor: pointer;
  color: ${({ theme }) => theme.colour.link.base};

  &:hover {
    text-decoration: underline;
  }
`

const OtherContacts = ({
  onClickViewContacts,
  numberOfOtherContacts,
}: {
  onClickViewContacts: () => void
  numberOfOtherContacts: number
}) => (
  <TableLink onClick={onClickViewContacts}>
    <Text style={{ fontSize: '0.7rem' }} type="secondary">
      <IconUser size={16} />
      <LinkSpan>
        {numberOfOtherContacts} other contact
        {numberOfOtherContacts > 1 && <>s</>}
      </LinkSpan>
    </Text>
  </TableLink>
)

const ContactList = ({
  onClickViewContacts,
  onClickAddContacts,
  onClickEditContact,
  contacts,
  showMobile,
  showAllProjectContacts,
}: ContactListProps) => {
  const { project } = useContext(ProjectContext)

  const primaryContacts = useMemo(() => {
    const projectContacts = contacts?.filter((c) =>
      c.projects?.map(({ id }) => id).includes(project?.id)
    )

    if (projectContacts?.length > 0) {
      return showAllProjectContacts ? projectContacts : [projectContacts[0]]
    }

    const defaultContact = contacts?.find((c) => c.isPrimaryContact)

    return defaultContact ? [defaultContact] : []
  }, [contacts, project, showAllProjectContacts])

  const numberOfOtherContacts = useMemo(
    () => contacts?.length - primaryContacts?.length,
    [contacts, primaryContacts]
  )

  if (primaryContacts?.length === 0) {
    return (
      <TableLink
        onClick={
          contacts?.length === 0 ? onClickAddContacts : onClickViewContacts
        }
      >
        <Tooltip title="Nominate a project contact to receive project communications.">
          <Text style={{ fontSize: '0.7rem' }}>
            <IconUserPlus size={16} />
            <LinkSpan>Add project contact</LinkSpan>
          </Text>
        </Tooltip>
      </TableLink>
    )
  }

  return (
    <>
      <Space direction={'vertical'}>
        <>
          {primaryContacts.map((primaryContact) => {
            const isValid = primaryContact.name || primaryContact.email

            if (!isValid) {
              return (
                <TableLink
                  key={primaryContact.id}
                  onClick={
                    onClickEditContact
                      ? () => onClickEditContact(primaryContact.id)
                      : undefined
                  }
                >
                  <Tooltip
                    title={`Contact does not have a name or an email.${
                      onClickEditContact
                        ? " Click to edit the contact's information."
                        : ''
                    }`}
                  >
                    <Text style={{ fontSize: '0.7rem' }} type="danger">
                      <IconUserPlus size={16} />
                      <LinkSpan>Invalid project contact</LinkSpan>
                    </Text>
                  </Tooltip>
                </TableLink>
              )
            }

            return (
              <ContactCard
                key={primaryContact.id || primaryContact.name}
                contact={primaryContact}
                showMobile={showMobile}
              />
            )
          })}
          {numberOfOtherContacts > 0 && (
            <OtherContacts
              onClickViewContacts={onClickViewContacts}
              numberOfOtherContacts={numberOfOtherContacts}
            />
          )}
        </>
      </Space>
    </>
  )
}

export default ContactList
